import { loadJSON } from '@antiopix/shared-ui/js/data-loader.js';
import { initCanvasFx } from '@antiopix/shared-ui/js/canvas-fx.js';
import { getContactFormHTML, bindContactFormEvents } from './contact.js';
import { getServicesHTML } from './services.js';
import { getPortfolioHTML, bindPortfolioFilters } from './portfolio.js';

/* ─── SVG icons (inline, no external dep) ─────────────────── */
const WA_ICON = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="width:18px;height:18px;fill:currentColor;flex-shrink:0;"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>`;

const WA_FAB_ICON = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#fff"/></svg>`;

function buildWhatsAppUrl(siteData) {
  const wa = siteData?.whatsapp;
  if (!wa || !wa.enabled) return null;
  const msg = encodeURIComponent(wa.default_message || "Hi Antiopix! I'd like to discuss a project.");
  return `https://wa.me/${wa.number}?text=${msg}`;
}

/* ─── Stats Row ───────────────────────────────────────────── */
function renderStats(stats = []) {
  if (!stats.length) return '';
  const items = stats.map((s, i) => `
    <div class="stat-item animate-on-scroll" style="animation-delay:${i * 0.12}s;">
      <span class="stat-value">${s.value}</span>
      <span class="stat-label">${s.label}</span>
    </div>
  `).join('');
  return `<div class="stats-row">${items}</div>`;
}

/* ─── Technologies Section ────────────────────────────────── */
function renderTechnologies(techData = {}) {
  const categories = Object.keys(techData);
  if (!categories.length) return '';

  const tabs = categories.map((cat, i) => `
    <button class="tech-tab ${i === 0 ? 'active' : ''}"
            data-tech-cat="${cat}"
            id="tech-tab-${cat}">
      ${cat.charAt(0).toUpperCase() + cat.slice(1)}
    </button>
  `).join('');

  const marquees = categories.map((cat, i) => {
    const pills = techData[cat];
    // Duplicate for seamless loop
    const pillsHTML = pills.map(p => `<span class="tech-pill">${p}</span>`).join('');
    return `
      <div class="tech-marquee-panel" data-tech-panel="${cat}" style="${i !== 0 ? 'display:none;' : ''}">
        <div class="tech-marquee-wrap">
          <div class="tech-marquee-track" id="tech-track-${cat}">
            ${pillsHTML}
            ${pillsHTML}
          </div>
        </div>
      </div>
    `;
  }).join('');

  return `
    <div class="section-header animate-on-scroll">
      <span class="section-eyebrow">Tech Stack</span>
      <h2 class="text-hero-display" style="font-size: clamp(2rem, 4vw, 3.25rem); margin-bottom: 1rem;">
        Built with World-Class Technologies
      </h2>
      <p style="font-size: 1.1rem; color: var(--text-secondary); max-width: 540px; margin: 0 auto;">
        We harness the power of modern tools to deliver fast, reliable, and scalable digital products.
      </p>
    </div>
    <div class="tech-tabs" id="tech-tabs">${tabs}</div>
    <div id="tech-panels">${marquees}</div>
  `;
}

function bindTechTabs() {
  const tabs = document.querySelectorAll('.tech-tab');
  const panels = document.querySelectorAll('.tech-marquee-panel');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const cat = tab.getAttribute('data-tech-cat');
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      panels.forEach(p => {
        p.style.display = p.getAttribute('data-tech-panel') === cat ? '' : 'none';
      });
    });
  });
}

/* ─── Floating WhatsApp FAB ───────────────────────────────── */
function renderFloatingFAB(siteData) {
  const wa = siteData?.whatsapp;
  if (!wa || !wa.enabled || !wa.show_fab) return;

  const fabContainer = document.getElementById('whatsapp-fab-container');
  if (!fabContainer) return;

  const waUrl = buildWhatsAppUrl(siteData);
  if (!waUrl) return;

  fabContainer.innerHTML = `
    <a href="${waUrl}" onclick="window.open(this.href, 'whatsapp', 'width=600,height=700'); return false;" class="whatsapp-fab" aria-label="Chat on WhatsApp">
      ${WA_FAB_ICON}
      <span class="whatsapp-fab-tooltip">Chat with us!</span>
    </a>
  `;
}

/* ─── Main Render ─────────────────────────────────────────── */
export async function renderPage() {
  const [homeData, siteData] = await Promise.all([
    loadJSON('/data/home.json'),
    loadJSON('/data/site.json')
  ]);
  if (!homeData) return;

  const content = document.getElementById('page-content');
  const waUrl = buildWhatsAppUrl(siteData);

  // Hero CTA resolution
  const heroPrimary = homeData.hero.cta_primary;
  const primaryHref = (heroPrimary.icon === 'whatsapp' && waUrl) ? waUrl : heroPrimary.href;
  const primaryTarget = (heroPrimary.icon === 'whatsapp') ? `onclick="window.open(this.href, 'whatsapp', 'width=600,height=700'); return false;"` : '';
  const primaryClass = heroPrimary.icon === 'whatsapp' ? 'btn btn-whatsapp' : 'btn btn-primary glow-button';

  // Stats
  const statsHTML = renderStats(homeData.hero.stats || []);

  // Values
  const valueNums = ['01', '02', '03', '04'];
  const valuesHTML = (homeData.values || []).map((val, i) => `
    <div class="value-card animate-on-scroll" style="animation-delay:${i * 0.1}s;">
      <div class="value-number">${valueNums[i] || String(i + 1).padStart(2, '0')}</div>
      <h3>${val.title}</h3>
      <p>${val.description}</p>
    </div>
  `).join('');

  // Technologies
  const techHTML = renderTechnologies(homeData.technologies || {});

  // Portfolio
  const portfolioHTML = getPortfolioHTML(homeData.portfolio || []);

  // Testimonials
  let testimonialsHTML = '';
  if (homeData.testimonials && homeData.testimonials.length > 0) {
    const cards = homeData.testimonials.map(t => `
      <div class="glass-card animate-on-scroll" style="position:relative; padding:2.5rem; text-align:left; display:flex; flex-direction:column; gap:1.25rem; border:1px solid rgba(255,255,255,0.05); overflow:hidden;">
        <div style="display:flex; gap:4px; margin-bottom:0.25rem;">
          ${'<span style="color:#f59e0b;font-size:1rem;">★</span>'.repeat(5)}
        </div>
        <svg style="position:absolute; top:1rem; right:1rem; width:52px; height:52px; opacity:0.05; fill:var(--text-primary);" viewBox="0 0 24 24"><path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/></svg>
        <p style="font-size:1.05rem; font-style:italic; color:var(--text-secondary); line-height:1.65; flex-grow:1;">"${t.quote}"</p>
        <div style="display:flex; align-items:center; gap:1rem;">
          <img src="${t.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=fallback'}" alt="${t.author}" style="width:48px;height:48px;border-radius:50%;object-fit:cover;border:2px solid var(--accent-primary);box-shadow:0 0 12px var(--glow-primary);">
          <div>
            <h4 style="color:var(--text-primary);font-size:0.95rem;margin:0;">${t.author}</h4>
            <span style="color:var(--text-secondary);font-size:0.82rem;">${t.role}</span>
          </div>
        </div>
      </div>
    `).join('');

    testimonialsHTML = `
      <section class="section" id="testimonials" style="padding-top:4rem;">
        <div class="section-header animate-on-scroll">
          <span class="section-eyebrow">Client Love</span>
          <h2 class="text-hero-display" style="font-size:clamp(2rem,4vw,3.25rem);margin-bottom:1rem;">What Our Clients Say</h2>
          <p style="font-size:1.1rem;color:var(--text-secondary);max-width:540px;margin:0 auto;">Real stories from partners we've elevated to new heights.</p>
        </div>
        <div class="grid" style="gap:2rem;">${cards}</div>
      </section>
    `;
  }

  content.innerHTML = `
    <!-- ── HERO ── -->
    <section class="hero" id="hero-section" style="position:relative; overflow:hidden; min-height:90vh; display:flex; flex-direction:column; align-items:center; justify-content:center;">
      <div class="animate-on-scroll" style="position:relative; z-index:1; text-align:center; max-width:820px; padding:0 1.5rem;">
        <h1 class="text-hero-display text-gradient">${homeData.hero.title}</h1>
        <p style="font-size:1.2rem; margin-bottom:2.5rem; color:var(--text-secondary); max-width:580px; margin-left:auto; margin-right:auto; line-height:1.65;">${homeData.hero.subtitle}</p>
        <div style="display:flex; gap:1.25rem; justify-content:center; flex-wrap:wrap;">
          <a href="${primaryHref}" class="${primaryClass}" ${primaryTarget} id="hero-cta-primary">
            ${WA_ICON}
            ${heroPrimary.label}
          </a>
          <a href="${homeData.hero.cta_secondary.href}" class="btn btn-outline" id="hero-cta-secondary">
            ${homeData.hero.cta_secondary.label} →
          </a>
        </div>
        ${statsHTML}
      </div>
      <div class="scroll-indicator" style="display:none;">
        <span>Scroll</span>
        <div class="scroll-arrow">↓</div>
      </div>
    </section>

    <!-- ── ABOUT / VALUES ── -->
    <section class="section" id="about" style="padding-top: 5rem; padding-bottom: 5rem;">
      <div class="section-header animate-on-scroll">
        <span class="section-eyebrow">Who We Are</span>
        <h2 class="text-hero-display" style="font-size:clamp(2rem,4vw,3.25rem);margin-bottom:1rem;">${homeData.about.title}</h2>
        <div style="max-width:700px;margin:0 auto;text-align:left;">
          ${homeData.about.paragraphs.map(p => `<p style="font-size:1.1rem;line-height:1.7;margin-top:1rem; color: var(--text-secondary);">${p}</p>`).join('')}
        </div>
      </div>
      <div class="grid" style="gap:1.5rem; margin-top:3rem;">
        ${(homeData.values || []).map((val, i) => `
          <div class="value-card animate-on-scroll glass-card" style="animation-delay:${i * 0.1}s;">
            <div class="value-number">${valueNums[i] || String(i + 1).padStart(2, '0')}</div>
            <h3>${val.title}</h3>
            <p style="color: var(--text-secondary);">${val.description}</p>
          </div>
        `).join('')}
      </div>
    </section>

    <!-- ── SERVICES ── -->
    <section class="section" id="services" style="padding-top:4rem;">
      ${getServicesHTML(homeData.services || [])}
    </section>

    <!-- ── PORTFOLIO ── -->
    <section class="section" id="portfolio" style="padding-top:4rem;">
      ${portfolioHTML}
    </section>

    <!-- ── TECHNOLOGIES ── -->
    <section class="section" id="technologies" style="padding-top:4rem; background: var(--bg-surface);">
      ${techHTML}
    </section>

    <!-- ── TESTIMONIALS ── -->
    ${testimonialsHTML}

    <!-- ── CONTACT ── -->
    <section class="section" id="contactUs" style="position:relative; overflow:hidden; padding-top:4rem; padding-bottom:6rem;">
      ${getContactFormHTML(siteData)}
    </section>
  `;

  // Init canvas particle effect on hero
  initCanvasFx('hero-section');

  // Bind form
  bindContactFormEvents();

  // Bind portfolio filters
  bindPortfolioFilters();

  // Bind tech tabs
  bindTechTabs();

  // Render floating FAB
  renderFloatingFAB(siteData);

  // Handle initial hash navigation
  if (window.location.hash) {
    const targetId = window.location.hash.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      setTimeout(() => targetElement.scrollIntoView({ behavior: 'smooth' }), 150);
    }
  }
}
